import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { Save } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApiUsersIdPatch } from '@r/api/generated/client.ts';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import type { UserMeReadModel } from '@r/api/generated/client.schemas.ts';
import { type FC, useEffect } from 'react';
import { queryClient } from '@r/utils/utils.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

export const PersonalInformationsForm: FC = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser().data;

  const FormSchema = z.object({
    lastname: z.string().min(1, t('form.fields.required')),
    firstname: z.string().min(1, t('form.fields.required')),
    email: z.string().min(1, t('form.fields.required')).email(t('form.field.invalid')),
    userWorkspace: z.object({
      phone: z.union([z.string(), z.null()]).optional()
    })
  });

  function mapUserDataToFormValues(user: UserMeReadModel): z.infer<typeof FormSchema> {
    return {
      lastname: user.lastname || '',
      firstname: user.firstname || '',
      email: user.email || '',
      userWorkspace: {
        phone: getActiveWorkspaceUniversal(user.visibleWorkspaces)?.phone || null
      }
    };
  }

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: user ? mapUserDataToFormValues(user) : {}
  });

  useEffect(() => {
    if (user) {
      form.reset(mapUserDataToFormValues(user));
    }
  }, [user, form]);

  const userMutation = useApiUsersIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('form.personal.informations.success'));
        queryClient.invalidateQueries({ queryKey: ['authenticated-user'] });
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('form.personal.informations.error'));
      }
    }
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    if (!user?.id) {
      toast.error(t('form.personal.informations.error'));
      return;
    }

    const activeWorkspace = getActiveWorkspaceUniversal(user.visibleWorkspaces);
    if (!activeWorkspace) {
      toast.error(t('form.personal.informations.error'));
      return;
    }

    userMutation.mutate({
      id: String(user.id),
      data: {
        lastname: data.lastname,
        firstname: data.firstname,
        email: data.email,
        userWorkspace: {
          phone: data.userWorkspace.phone || null,
          status: activeWorkspace.status
        }
      }
    });
  }

  return (
    <Form {...form} schema={FormSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="lastname"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('personal.information.lastname')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('personal.information.lastname')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="firstname"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('personal.information.firstname')}</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t('personal.information.firstname')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('personal.information.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t('personal.information.email')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="userWorkspace.phone"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('personal.information.phone')}</FormLabel>
                <FormControl>
                  <Input
                    type="tel"
                    placeholder={t('personal.information.phone')}
                    {...field}
                    error={!!fieldState.error}
                    value={field.value || ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          rounded="full"
          loading={userMutation.isPending}
          className="w-full md:w-auto"
          type="submit"
        >
          <Save className="mr-2 w-4 h-4" />
          {t('form.personal.informations.save')}
        </Button>
      </form>
    </Form>
  );
};
